// user guide 


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Undo2, Redo2, Link, Image, Video, Code, Minus, FileText, Edit, Eye, Globe, Smartphone, Languages, ChevronLeft, ChevronRight, Search, Filter } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/Select';
import { Modal } from './ui/Modal';
import { userGuideService } from '../api/userGuideService';
// import { roleService } from '../api/roleService'; // Commented out - Role field hiddenchanges done
import toast from 'react-hot-toast';

const parseApiList = (response) => {
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const parseApiEntity = (response) => {
  if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
    return response.data;
  }
  if (response?.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return null;
};

const parseScreensFromResponse = (response) => {
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const extractHtmlContent = (guide) => {
  if (!guide?.html_content) {
    return '';
  }
  if (typeof guide.html_content === 'string') {
    return guide.html_content;
  }
  if (guide.html_content.content) {
    return guide.html_content.content;
  }
  if (guide.html_content.html) {
    return guide.html_content.html;
  }
  return '';
};

const UserGuide = () => {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const screensByModuleCacheRef = useRef(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingGuide, setViewingGuide] = useState(null); // Track which guide is being viewed
  const [userGuides, setUserGuides] = useState([]);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null); // Track which guide is being edited
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [applicationTypeFilter, setApplicationTypeFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  
  // Hidden file input refs for image and video
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Form state
  const [moduleId, setModuleId] = useState('');
  const [screenId, setScreenId] = useState('');
  // const [roleId, setRoleId] = useState(''); // Commented out - Role field hidden
  const [applicationType, setApplicationType] = useState('Web');
  const [languageId, setLanguageId] = useState('1'); // Default to English
  const [htmlContent, setHtmlContent] = useState('');
  
  // Dropdown data
  const [modules, setModules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [allScreensMap, setAllScreensMap] = useState(new Map()); // Map of screen_id -> screen object for display
  // const [roles, setRoles] = useState([]); // Commented out - Role field hidden
  const [languages] = useState([
    { id: 1, name: 'English' },
    { id: 2, name: 'Spanish' },
    { id: 3, name: 'French' },
    { id: 4, name: 'German' },
    { id: 5, name: 'Hindi' }
  ]);
  
  // Rich text editor state
  const [editorContent, setEditorContent] = useState('');

  useEffect(() => {
    initializePageData();
    // loadRoles(); // Commented out - Role field hidden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (moduleId) {
      loadScreens(moduleId);
    } else {
      setScreens([]);
      setScreenId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  // Set screenId when editing and screens are loaded
  useEffect(() => {
    if (editingGuide && screens.length > 0) {
      const expectedScreenId = editingGuide.screen_id?.toString() || '';
      // Only set if it's not already set correctly
      if (expectedScreenId && screenId !== expectedScreenId) {
        setScreenId(expectedScreenId);
      }
    }
  }, [screens, editingGuide, screenId]);

  // Reset page when filters change and current page is out of bounds
  useEffect(() => {
    if (userGuides.length > 0) {
      // Calculate filtered guides to check total pages
      const filteredGuides = userGuides.filter((guide) => {
        const module = modules.find(m => m.id === guide.menu_id);
        const moduleName = module?.menumoduleName || '';
        
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          if (!moduleName.toLowerCase().includes(searchLower) && 
              !guide.screen_id?.toString().includes(searchLower)) {
            return false;
          }
        }
        
        if (statusFilter !== 'all') {
          if (statusFilter === 'active' && !guide.is_active) return false;
          if (statusFilter === 'inactive' && guide.is_active) return false;
        }
        
        if (applicationTypeFilter !== 'all') {
          if (applicationTypeFilter === 'web' && !guide.is_web_content) return false;
          if (applicationTypeFilter === 'mobile' && guide.is_web_content) return false;
        }
        
        if (languageFilter !== 'all') {
          if (guide.language_id?.toString() !== languageFilter) return false;
        }
        
        return true;
      });
      
      const totalPages = Math.ceil(filteredGuides.length / pageSize);
      if (currentPage >= totalPages && totalPages > 0) {
        setCurrentPage(0);
      }
    }
  }, [searchTerm, statusFilter, applicationTypeFilter, languageFilter, userGuides, modules, pageSize, currentPage]);

  const mergeScreensIntoMap = (screensMap, screensData) => {
    screensData.forEach((screen) => {
      if (screen.id) {
        screensMap.set(screen.id, screen);
        screensMap.set(String(screen.id), screen);
      }
    });
  };

  const fetchScreensForModule = async (moduleId) => {
    const cacheKey = String(moduleId);
    if (screensByModuleCacheRef.current.has(cacheKey)) {
      return screensByModuleCacheRef.current.get(cacheKey);
    }
    const response = await userGuideService.getScreensByModuleId(moduleId);
    const screensData = parseScreensFromResponse(response);
    screensByModuleCacheRef.current.set(cacheKey, screensData);
    return screensData;
  };

  const loadAllScreensForGuides = async (guidesData) => {
    try {
      const uniqueModuleIds = [...new Set(guidesData.map((guide) => guide.menu_id).filter(Boolean))];
      const screensMap = new Map();
      await Promise.all(
        uniqueModuleIds.map(async (modId) => {
          try {
            const screensData = await fetchScreensForModule(modId);
            mergeScreensIntoMap(screensMap, screensData);
          } catch (error) {
            console.warn(`Failed to load screens for module ${modId}:`, error);
          }
        })
      );
      setAllScreensMap(screensMap);
    } catch (error) {
      console.error('Error loading screens for guides:', error);
    }
  };

  const initializePageData = async () => {
    setLoadingGuides(true);
    try {
      const [modulesResponse, guidesResponse] = await Promise.all([
        userGuideService.getMenuModules(),
        userGuideService.getUserGuideMappingsSummary()
      ]);
      const modulesData = parseApiList(modulesResponse);
      const guidesData = parseApiList(guidesResponse);
      setModules(modulesData);
      setUserGuides(guidesData);
      if (guidesData.length > 0) {
        await loadAllScreensForGuides(guidesData);
      } else {
        setAllScreensMap(new Map());
      }
      const totalPages = Math.ceil(guidesData.length / pageSize);
      if (currentPage >= totalPages && totalPages > 0) {
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Error initializing user guide page:', error);
      toast.error('Failed to load user guide data');
      setModules([]);
      setUserGuides([]);
      setAllScreensMap(new Map());
    } finally {
      setLoadingGuides(false);
    }
  };

  const loadScreens = async (modId) => {
    if (!modId) {
      setScreens([]);
      return;
    }
    setLoading(true);
    try {
      const screensData = await fetchScreensForModule(modId);
      setScreens(screensData);
    } catch (error) {
      console.error('Error loading screens:', error);
      toast.error('Failed to load screens');
      setScreens([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserGuides = async () => {
    setLoadingGuides(true);
    try {
      const response = await userGuideService.getUserGuideMappingsSummary();
      const guidesData = parseApiList(response);
      setUserGuides(guidesData);
      if (guidesData.length > 0) {
        await loadAllScreensForGuides(guidesData);
      } else {
        setAllScreensMap(new Map());
      }
      const totalPages = Math.ceil(guidesData.length / pageSize);
      if (currentPage >= totalPages && totalPages > 0) {
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Error loading user guides from API:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load user guides';
      toast.error(errorMessage);
      setUserGuides([]);
    } finally {
      setLoadingGuides(false);
    }
  };

  const fetchFullUserGuide = async (guide) => {
    const guideId = guide?.id ?? guide?.userGuideMappingId;
    if (!guideId) {
      throw new Error('User guide id is missing');
    }
    const response = await userGuideService.getUserGuideMappingById(guideId);
    const fullGuide = parseApiEntity(response);
    if (!fullGuide) {
      throw new Error('User guide not found');
    }
    return fullGuide;
  };

  // Commented out - Role field hidden
  // const loadRoles = async () => {
  //   try {
  //     const response = await roleService.getRoles();
  //     // Handle different response structures
  //     let rolesData = [];
  //     if (response?.data?.data) {
  //       rolesData = Array.isArray(response.data.data) ? response.data.data : [];
  //     } else if (response?.data) {
  //       rolesData = Array.isArray(response.data) ? response.data : [];
  //     } else if (Array.isArray(response)) {
  //       rolesData = response;
  //     }
  //     setRoles(rolesData);
  //   } catch (error) {
  //     console.error('Error loading roles:', error);
  //     // Don't show error toast for roles as it's optional
  //     setRoles([]);
  //   }
  // };

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateEditorContent();
  };

  const updateEditorContent = () => {
    if (editorRef.current) {
      setEditorContent(editorRef.current.innerHTML);
      setHtmlContent(editorRef.current.innerHTML);
    }
  };

  const handleEditorInput = () => {
    updateEditorContent();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    updateEditorContent();
  };

  // Handle image file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert file to base64 data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        // Insert image into editor
        execCommand('insertImage', dataUrl);
        toast.success('Image inserted successfully');
        setUploadingImage(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setUploadingImage(false);
    } finally {
      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Handle video file upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error('Video size should be less than 50MB');
      return;
    }

    setUploadingVideo(true);
    try {
      // Convert file to base64 data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        // Insert video into editor using HTML5 video tag
        const videoHtml = `<video controls width="560" height="315" style="max-width: 100%; height: auto;">
          <source src="${dataUrl}" type="${file.type}">
          Your browser does not support the video tag.
        </video>`;
        document.execCommand('insertHTML', false, videoHtml);
        updateEditorContent();
        toast.success('Video inserted successfully');
        setUploadingVideo(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read video file');
        setUploadingVideo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
      setUploadingVideo(false);
    } finally {
      // Reset file input
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!moduleId) {
      toast.error('Please select a Module Name');
      return;
    }
    if (!screenId) {
      toast.error('Please select a Screen Name');
      return;
    }
    if (!applicationType) {
      toast.error('Please select an Application Type');
      return;
    }
    if (!languageId) {
      toast.error('Please select a Language');
      return;
    }

    const content = htmlContent || editorContent || '';
    if (!content.trim()) {
      toast.error('Please enter some content in the editor');
      return;
    }

    setSaving(true);
    try {
      // The backend expects htmlContent as JsonNode (JSON format)
      // We can send it as a simple object or as HTML string - backend will handle it
      // Based on the entity, it accepts JsonNode, so we'll send it as an object
      const htmlContentJson = content ? {
        content: content,
        type: 'html',
        created: new Date().toISOString()
      } : null;

      const payload = {
        menu_id: parseInt(moduleId),
        screen_id: parseInt(screenId),
        // role_id: roleId ? parseInt(roleId) : null, // Commented out - Role field hidden
        file_type_id: applicationType === 'Web' ? 1 : 2, // 1 for Web, 2 for Mobile
        is_web_content: applicationType === 'Web',
        language_id: parseInt(languageId),
        language_name: languages.find(l => l.id === parseInt(languageId))?.name || 'English',
        html_content: htmlContentJson, // Send as JSON object for JsonNode
        is_active: editingGuide?.is_active !== undefined ? editingGuide.is_active : true
      };

      // Include ID if editing
      if (editingGuide?.id || editingGuide?.userGuideMappingId) {
        payload.id = editingGuide.id || editingGuide.userGuideMappingId;
      }

      const response = await userGuideService.saveUserGuideMapping(payload);
      
      // Handle ResponseBean structure: { status, message, data }
      const statusCode = response?.data?.status?.value || response?.data?.status || response?.status?.value || response?.status;
      const isSuccess = response?.status === 'OK'? true : false;
      
      if (isSuccess) {
        const successMessage = editingGuide 
          ? (response?.data?.message || 'User guide updated successfully')
          : (response?.data?.message || 'User guide saved successfully');
        toast.success(successMessage);
        // Reset form and close modal
        resetForm();
        setIsModalOpen(false);
        // Reload user guides list and reset to first page
        setCurrentPage(0);
        loadUserGuides();
      } else {
        const errorMessage = editingGuide
          ? (response?.data?.message || response?.message || 'Failed to update user guide')
          : (response?.data?.message || response?.message || 'Failed to save user guide');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving user guide:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.data?.message ||
                          error.message || 
                          'Failed to save user guide';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Function to reset form for adding new user guide
  const resetForm = () => {
    setModuleId('');
    setScreenId('');
    // setRoleId(''); // Commented out - Role field hidden
    setApplicationType('Web');
    setLanguageId('1');
    setEditorContent('');
    setHtmlContent('');
    setEditingGuide(null);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  const handleAddNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleEdit = async (guide) => {
    try {
      setLoading(true);
      const fullGuide = await fetchFullUserGuide(guide);
      setEditingGuide(fullGuide);

      const moduleIdStr = fullGuide.menu_id?.toString() || '';
      setScreenId('');
      setModuleId(moduleIdStr);
      setApplicationType(fullGuide.is_web_content ? 'Web' : 'Mobile');
      setLanguageId(fullGuide.language_id?.toString() || '1');

      const content = extractHtmlContent(fullGuide);
      setEditorContent(content);
      setHtmlContent(content);

      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = content;
        }
      }, 100);

      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading user guide for edit:', error);
      toast.error(error?.message || 'Failed to load user guide for editing');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!saving) {
      setIsModalOpen(false);
      setEditingGuide(null);
    }
  };

  const handleView = async (guide) => {
    try {
      setLoading(true);
      const fullGuide = await fetchFullUserGuide(guide);
      setViewingGuide(fullGuide);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Error loading user guide for view:', error);
      toast.error(error?.message || 'Failed to load user guide content');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingGuide(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">User Guide</h1>
          <Button
            variant="default"
            onClick={handleAddNew}
            className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
      </div>

      {/* Main Content - User Guides Table */}
      {!isModalOpen && (
        <div className="w-full py-6">
          {loadingGuides ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : userGuides && userGuides.length > 0 ? (
            <>
              {/* Filters */}
              <div className="mb-4 px-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-3 flex-nowrap">
                    {/* Search */}
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by module or screen..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(0);
                        }}
                        className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Status Filter */}
                    <div className="flex-shrink-0">
                      <Select value={statusFilter} onValueChange={(value) => {
                        setStatusFilter(value);
                        setCurrentPage(0);
                      }}>
                        <SelectTrigger className="h-10 min-w-[140px]">
                          <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Application Type Filter */}
                    <div className="flex-shrink-0">
                      <Select value={applicationTypeFilter} onValueChange={(value) => {
                        setApplicationTypeFilter(value);
                        setCurrentPage(0);
                      }}>
                        <SelectTrigger className="h-10 min-w-[140px]">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="web">Web</SelectItem>
                          <SelectItem value="mobile">Mobile</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Language Filter */}
                    <div className="flex-shrink-0">
                      <Select value={languageFilter} onValueChange={(value) => {
                        setLanguageFilter(value);
                        setCurrentPage(0);
                      }}>
                        <SelectTrigger className="h-10 min-w-[160px]">
                          <SelectValue placeholder="All Languages" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Languages</SelectItem>
                          {languages.map((lang) => (
                            <SelectItem key={lang.id} value={lang.id.toString()}>
                              {lang.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter and calculate filtered data */}
              {(() => {
                // Apply filters
                let filteredGuides = userGuides.filter((guide) => {
                  const module = modules.find(m => m.id === guide.menu_id);
                  const moduleName = module?.menumoduleName || '';
                  
                  // Search filter
                  if (searchTerm) {
                    const searchLower = searchTerm.toLowerCase();
                    if (!moduleName.toLowerCase().includes(searchLower) && 
                        !guide.screen_id?.toString().includes(searchLower)) {
                      return false;
                    }
                  }
                  
                  // Status filter
                  if (statusFilter !== 'all') {
                    if (statusFilter === 'active' && !guide.is_active) return false;
                    if (statusFilter === 'inactive' && guide.is_active) return false;
                  }
                  
                  // Application type filter
                  if (applicationTypeFilter !== 'all') {
                    if (applicationTypeFilter === 'web' && !guide.is_web_content) return false;
                    if (applicationTypeFilter === 'mobile' && guide.is_web_content) return false;
                  }
                  
                  // Language filter
                  if (languageFilter !== 'all') {
                    if (guide.language_id?.toString() !== languageFilter) return false;
                  }
                  
                  return true;
                });
                
                const totalFiltered = filteredGuides.length;
                const totalPages = Math.ceil(totalFiltered / pageSize);
                // Use safe current page (reset to 0 if out of bounds)
                const safeCurrentPage = currentPage >= totalPages && totalPages > 0 ? 0 : currentPage;
                const paginatedGuides = filteredGuides.slice(safeCurrentPage * pageSize, (safeCurrentPage + 1) * pageSize);
                
                return (
                  <>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                S.No
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Module
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                 Screen
                              </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Application Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Language
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Created
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedGuides.map((guide, index) => {
                            const module = modules.find(m => m.id === guide.menu_id);
                            const language = languages.find(l => l.id === guide.language_id);
                            const createdDate = guide.created_at || guide.createdAt || guide.created_date;
                            const rowIndex = (safeCurrentPage * pageSize) + index + 1;
                            
                            return (
                              <tr key={guide.id || guide.userGuideMappingId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {rowIndex}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                <FileText className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {module?.menumoduleName || `Module ID: ${guide.menu_id}`}
                                </div>
                               
                              </div>
                            </div>
                          </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                      <FileText className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {allScreensMap.get(guide.screen_id)?.screenLable || 
                                         allScreensMap.get(parseInt(guide.screen_id))?.screenLable ||
                                         allScreensMap.get(guide.screen_id)?.screenName ||
                                         allScreensMap.get(parseInt(guide.screen_id))?.screenName ||
                                         `Screen ID: ${guide.screen_id}`}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {guide.is_web_content ? (
                                <Globe className="h-4 w-4 text-blue-500" />
                              ) : (
                                <Smartphone className="h-4 w-4 text-purple-500" />
                              )}
                              <span className="text-sm text-gray-900 dark:text-white">
                                {guide.is_web_content ? 'Web' : 'Mobile'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Languages className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {language?.name || 'English'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              guide.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {guide.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {createdDate ? new Date(createdDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(guide)}
                                className="h-8 w-8 p-0 border-green-200 hover:bg-green-50 hover:border-green-300"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleView(guide)}
                                className="h-8 w-8 p-0 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                                title="View"
                              >
                                <Eye className="h-4 w-4 text-blue-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination Controls - Bottom */}
                  {totalPages > 1 && (
                    <div className="mt-4 px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Showing <strong className="text-gray-900 dark:text-white">{Math.min((safeCurrentPage * pageSize) + 1, totalFiltered)}</strong> to <strong className="text-gray-900 dark:text-white">{Math.min((safeCurrentPage + 1) * pageSize, totalFiltered)}</strong> of <strong className="text-gray-900 dark:text-white">{totalFiltered}</strong> user guides
                        {totalFiltered !== userGuides.length && (
                          <span className="ml-2 text-xs">(filtered from {userGuides.length} total)</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                          disabled={safeCurrentPage === 0 || loadingGuides}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <span className="text-gray-600 dark:text-gray-400 px-2">
                          {safeCurrentPage + 1} / {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                          disabled={safeCurrentPage >= totalPages - 1 || loadingGuides}
                          className="flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="p-4 bg-teal-100 dark:bg-teal-900/30 rounded-full">
                  <Plus className="h-12 w-12 text-teal-600 dark:text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  No User Guides Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  Click the "Add" button above to create your first user guide entry.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal for Add/Edit Form */}
      <Modal
        isOpen={isModalOpen}
        onClose={saving ? undefined : handleCloseModal}
        title={editingGuide ? "Edit User Guide" : "Add User Guide"}
        size="full"
      >
        <div className="space-y-6">
        {/* Form Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Module Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Module Name :<span className="text-red-500">*</span>
              </label>
              <Select value={moduleId} onValueChange={setModuleId} disabled={loading}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Module" />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((module) => (
                    <SelectItem key={module.id} value={module.id.toString()}>
                      {module.menumoduleName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Screen Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Screen Name :<span className="text-red-500">*</span>
              </label>
              <Select 
                value={screenId} 
                onValueChange={setScreenId} 
                disabled={loading || !moduleId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={moduleId ? "Select Screen" : "Select Module first"} />
                </SelectTrigger>
                <SelectContent>
                  {screens.map((screen) => (
                    <SelectItem key={screen.id} value={screen.id.toString()}>
                      {screen.screenLable || screen.screenName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Application Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Application Type :<span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="applicationType"
                    value="Web"
                    checked={applicationType === 'Web'}
                    onChange={(e) => setApplicationType(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Web</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="applicationType"
                    value="Mobile"
                    checked={applicationType === 'Mobile'}
                    onChange={(e) => setApplicationType(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Mobile</span>
                </label>
              </div>
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Languages :<span className="text-red-500">*</span>
              </label>
              <Select value={languageId} onValueChange={setLanguageId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.id} value={lang.id.toString()}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role ID - Commented out - Role field hidden */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Role :<span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <Select value={roleId} onValueChange={setRoleId} disabled={loading}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Role (Optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None (Global)</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.roleId || role.id} value={(role.roleId || role.id).toString()}>
                      {role.roleName || role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div> */}
          </div>
        </div>

        {/* Rich Text Editor */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Toolbar */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-2">
            {/* First Row */}
            <div className="flex flex-wrap items-center gap-1 mb-2">
              {/* Undo/Redo */}
              <button
                type="button"
                onClick={() => execCommand('undo')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Undo"
              >
                <Undo2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('redo')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Redo"
              >
                <Redo2 className="h-4 w-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Text Formatting */}
              <button
                type="button"
                onClick={() => execCommand('bold')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-bold"
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('italic')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded italic"
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('underline')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded underline"
                title="Underline"
              >
                <Underline className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('strikeThrough')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded line-through"
                title="Strikethrough"
              >
                <Strikethrough className="h-4 w-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Alignment */}
              <button
                type="button"
                onClick={() => execCommand('justifyLeft')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded bg-yellow-100"
                title="Align Left"
              >
                <AlignLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('justifyCenter')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Align Center"
              >
                <AlignCenter className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('justifyRight')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Align Right"
              >
                <AlignRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('justifyFull')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Justify"
              >
                <AlignJustify className="h-4 w-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Lists */}
              <button
                type="button"
                onClick={() => execCommand('insertUnorderedList')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Bullet List"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('insertOrderedList')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Numbered List"
              >
                <ListOrdered className="h-4 w-4" />
              </button>

              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

              {/* Paragraph Style Dropdown */}
              <select
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'p') {
                    execCommand('formatBlock', '<p>');
                  } else if (value === 'h1') {
                    execCommand('formatBlock', '<h1>');
                  } else if (value === 'h2') {
                    execCommand('formatBlock', '<h2>');
                  } else if (value === 'h3') {
                    execCommand('formatBlock', '<h3>');
                  }
                }}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                title="Paragraph Style"
                defaultValue="p"
              >
                <option value="p">Standard</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
              </select>

              {/* Font Family Dropdown */}
              <select
                onChange={(e) => execCommand('fontName', e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                title="Font Family"
                defaultValue="Arial"
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Verdana">Verdana</option>
                <option value="Georgia">Georgia</option>
              </select>

              {/* Font Size Dropdown */}
              <select
                onChange={(e) => execCommand('fontSize', e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                title="Font Size"
                defaultValue="3"
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </select>

              {/* Text Color */}
              <input
                type="color"
                onChange={(e) => execCommand('foreColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                title="Text Color"
              />

              {/* Highlight Color */}
              <input
                type="color"
                onChange={(e) => execCommand('backColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                title="Highlight Color"
              />

              {/* Clear Class Dropdown */}
              <select
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                title="Clear Class"
                defaultValue=""
              >
                <option value="">Clear Class</option>
              </select>
            </div>

            {/* Second Row */}
            <div className="flex flex-wrap items-center gap-1">
              {/* Link */}
              <button
                type="button"
                onClick={() => {
                  const url = prompt('Enter URL:');
                  if (url) execCommand('createLink', url);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Insert Link"
              >
                <Link className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('unlink')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Remove Link"
              >
                <Link className="h-4 w-4 rotate-45" />
              </button>

              {/* Image */}
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50"
                title="Insert Image"
              >
                {uploadingImage ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                ) : (
                  <Image className="h-4 w-4" />
                )}
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />

              {/* Video */}
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50"
                title="Insert Video"
              >
                {uploadingVideo ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                ) : (
                  <Video className="h-4 w-4" />
                )}
              </button>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleVideoUpload}
              />

              {/* Horizontal Rule */}
              <button
                type="button"
                onClick={() => {
                  document.execCommand('insertHorizontalRule', false);
                  updateEditorContent();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Insert Horizontal Rule"
              >
                <Minus className="h-4 w-4" />
              </button>

              {/* Remove Formatting */}
              <button
                type="button"
                onClick={() => execCommand('removeFormat')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Remove Formatting"
              >
                <Code className="h-4 w-4" />
              </button>

              {/* Code View */}
              <button
                type="button"
                onClick={() => {
                  if (editorRef.current) {
                    const isCodeView = editorRef.current.contentEditable === 'false';
                    if (isCodeView) {
                      editorRef.current.contentEditable = 'true';
                      editorRef.current.innerHTML = editorRef.current.textContent;
                    } else {
                      editorRef.current.contentEditable = 'false';
                      editorRef.current.textContent = editorRef.current.innerHTML;
                    }
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Code View"
              >
                <Code className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Editor Content Area */}
          <div
            ref={editorRef}
            contentEditable
            onInput={handleEditorInput}
            onPaste={handlePaste}
            className="min-h-[400px] p-4 text-gray-900 dark:text-gray-100 focus:outline-none"
            style={{
              border: 'none',
              outline: 'none'
            }}
            data-placeholder="Enter text here..."
          />
          <style>{`
            [contenteditable][data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #9ca3af;
              pointer-events: none;
            }
          `}</style>
        </div>

        {/* Submit Button */}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || loading}
            className="px-6"
          >
                       {saving ? (editingGuide ? 'Updating...' : 'Saving...') : (editingGuide ? 'Update User Guide' : 'Save User Guide')}
          </Button>
        </div>
      </div>
    </Modal>

      {/* View Modal for User Guide */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        title="View User Guide"
        size="full"
      >
        {viewingGuide && (
          <div className="space-y-6">
            {/* Guide Information */}

            {/* Content Display */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content</h3>
              </div>
              <div className="p-6">
                <div 
                  className="max-w-none min-h-[fit-content] text-gray-900 dark:text-gray-100 prose prose-sm sm:prose lg:prose-lg xl:prose-xl dark:prose-invert max-w-none"
                  style={{
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: extractHtmlContent(viewingGuide) || '<p class="text-gray-500 dark:text-gray-400 italic">No content available</p>'
                  }}
                />
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={handleCloseViewModal}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserGuide;

